import React from 'react'
import styled from '@emotion/styled'

import { textCrop } from '../GlobalStyles/typography'
import { useScreenSize } from '../../hooks/useScreenSize'
import useResponsiveProp from '../../hooks/useResponsiveProp'

export interface TextProps {
  component?: 'p' | 'span' | 'label'
  children?: React.ReactNode
  size?: ResponsiveProp<'tiny' | 'small' | 'base' | 'big' | 'bigger' | 'huge'>
  weight?: ResponsiveProp<'regular' | 'strong' | 'stronger' | 'fat'>
  tone?: ResponsiveProp<
    | 'light'
    | 'neutral'
    | 'action'
    | 'positive'
    | 'negative'
    | 'caution'
    | 'contrast'
    | 'ghost'
    | 'info'
  >
  align?: ResponsiveProp<'left' | 'center' | 'right'>
  truncate?: boolean
  htmlFor?: string
  pLineHeight?: ResponsiveProp<number>
  strikethrough?: boolean
  underline?: boolean
}

export const Text: React.FC<TextProps> = ({
  component = 'span',
  children,
  size,
  weight = 'regular',
  tone = 'neutral',
  align = 'left',
  pLineHeight = 1.3,
  truncate = false,
  ...props
}) => {
  const device = useScreenSize()

  size = useResponsiveProp(size, device)
  weight = useResponsiveProp(weight, device)
  tone = useResponsiveProp(tone, device)
  align = useResponsiveProp(align, device)
  pLineHeight = useResponsiveProp(pLineHeight, device)

  const content = truncate ? <TruncateSpan>{children}</TruncateSpan> : children

  return (
    <StyledText
      as={component}
      pLineHeight={pLineHeight}
      size={size}
      weight={weight}
      tone={tone}
      align={align}
      {...props}
    >
      {content}
    </StyledText>
  )
}

const TruncateSpan = styled('span')`
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding-bottom: 2px;
  margin-bottom: -2px;
`

const StyledText = styled('div')<TextProps>`
  width: 100%;
  display: block;

  ${(p) => (p.as === 'p' ? textCrop(p.pLineHeight as number, 2, 1) : textCrop())}

  ${(p) => `
    text-align: ${p.align};
    font-weight: var(--font-weight--${p.weight});
    color: ${p.tone === 'caution' ? `var(--colors-${p.tone}--dark)` : `var(--colors-${p.tone})`};
  `}

  ${(p) =>
    p.size &&
    `
    font-size: var(--font-size--${p.size});
  `}

  ${(p) =>
    p.strikethrough &&
    `
    text-decoration: line-through;
  `}

  ${(p) =>
    p.underline &&
    `
    text-decoration: underline;
  `}
`
